import {phoneNumberFormat} from "@pg-mono/string-utils";
import {CONSULTANT_PHONE_NATIONAL} from "@pg-rp/consultants";

import {ReservationConfirmedVariant} from "../types/IReservationConfirmedStepContentProps";
import {IReservationError, ReservationErrorKind} from "../types/IReservationError";
import {FULL_NAME_PART_MAX_LENGTH} from "./reservation_constants";

// the copy links it with CONSULTANT_PHONE_HREF
export const RESERVATION_HOTLINE = phoneNumberFormat(CONSULTANT_PHONE_NATIONAL);

// m:ss, e.g. "0:42"
export const formatReservationCountdown = (seconds: number) => {
    const wholeSeconds = Math.max(0, Math.ceil(seconds));

    return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, "0")}`;
};

// Code step

export const RESERVATION_CODE_STEP_TITLE = "Wpisz kod z e-maila";
// label of the dialog and of its loader while the first code is being sent - the title is not rendered yet
export const RESERVATION_SENDING_CODE_LABEL = "Wysyłamy kod";

export const RESERVATION_RESEND_QUESTION = "Nie dostałeś kodu?";
export const RESERVATION_RESEND_LINK_TEXT = "Wyślij ponownie";
export const RESERVATION_RESEND_EXHAUSTED_TEXT = `Limit kodów wyczerpany — spróbuj za 10 minut lub zadzwoń ${RESERVATION_HOTLINE}.`;
// announced once a resent code is on its way
export const RESERVATION_NEW_CODE_SENT_TEXT = "Wysłaliśmy nowy kod.";

// Countdowns - the ticking m:ss is rendered between `before` and `after`, apart from the text, so that it stays out of translation
export const RESERVATION_RESEND_COOLDOWN_COPY = {before: "Wyślij ponownie za ", after: ""};
export const RESERVATION_RESEND_BLOCKED_COPY = {before: "Zbyt wiele prób — spróbuj ponownie za ", after: "."};
// a rate-limited start counts down until "Spróbuj ponownie" becomes available
export const RESERVATION_RATE_LIMITED_COPY = {before: "Odczekaj ", after: " i spróbuj ponownie."};
export const RESERVATION_RATE_LIMIT_OVER_TEXT = "Możesz już spróbować ponownie.";

export const RESERVATION_DATA_ADMINISTRATOR_TEXT = "Administratorem danych jest Property Group sp. z o.o.";
export const RESERVATION_TERMS_LINK_TEXT = "Regulamin";

// Name step - shown when the API rejects the reservation for the profile name

export const RESERVATION_NAME_STEP_TITLE = "Uzupełnij swoje dane";
export const RESERVATION_NAME_STEP_DESCRIPTION = "Aby zarezerwować mieszkanie, podaj imię i nazwisko — przekażemy je deweloperowi.";
export const RESERVATION_NAME_INPUT_LABEL = "Imię i nazwisko";
export const RESERVATION_NAME_SUBMIT_BUTTON_TEXT = "Zapisz i wyślij kod";
export const RESERVATION_NAME_REQUIRED_TEXT = "Podaj imię i nazwisko.";
export const RESERVATION_NAME_TOO_LONG_TEXT = `Imię i nazwisko mogą mieć najwyżej ${FULL_NAME_PART_MAX_LENGTH} znaków każde.`;
export const RESERVATION_NAME_SAVE_FAILED_TEXT = "Nie udało się zapisać danych. Spróbuj ponownie.";

// Errors

// `IReservationError.message` per kind - a short plain-text message from the API replaces it for some kinds
export const RESERVATION_ERROR_MESSAGES: Record<ReservationErrorKind, string> = {
    invalid_code: "Nieprawidłowy kod — sprawdź e-mail i spróbuj ponownie.",
    expired: "Kod wygasł — wyślij ponownie.",
    taken: "Ta nieruchomość została już zarezerwowana.",
    user_limit: "Wykorzystałeś limit rezerwacji tej nieruchomości.",
    not_reservable: "Tej nieruchomości nie można teraz zarezerwować.",
    rate_limited: "Odczekaj chwilę i spróbuj ponownie.",
    not_verified: `Zweryfikuj konto, aby zarezerwować bezpłatnie. Pomoc: ${RESERVATION_HOTLINE}.`,
    profile_incomplete: "Uzupełnij imię i nazwisko w profilu, aby zarezerwować bezpłatnie.",
    rejected: `Nie możemy teraz zarezerwować tego lokalu. Pomoc: ${RESERVATION_HOTLINE}.`,
    unauthenticated: "Zaloguj się, aby zarezerwować bezpłatnie.",
    csrf: "Sesja wygasła — odśwież stronę i spróbuj ponownie.",
    forbidden: `Nie masz uprawnień do rezerwacji tego lokalu. Pomoc: ${RESERVATION_HOTLINE}.`,
    validation: "Sprawdź dane i spróbuj ponownie.",
    network: "Brak połączenia. Sprawdź internet i spróbuj ponownie.",
    server: "Chwilowy problem po stronie systemu rezerwacji. Spróbuj ponownie za chwilę.",
    unknown: `Spróbuj ponownie lub zadzwoń ${RESERVATION_HOTLINE}.`
};

// the expired-code copy asks for a resend, which is gone once every code of the CRM's window has been sent
export const RESERVATION_EXPIRED_WITHOUT_RESEND_TEXT = "Kod wygasł.";

const RESERVATION_FAILED_TITLES: Partial<Record<ReservationErrorKind, string>> = {
    rate_limited: "Zbyt wiele prób",
    // not about somebody else - the reservation can be the user's own earlier one
    taken: "Lokal jest już zarezerwowany",
    user_limit: "Limit rezerwacji wykorzystany",
    not_reservable: "Rezerwacja niedostępna",
    not_verified: "Twoje konto wymaga weryfikacji",
    profile_incomplete: RESERVATION_NAME_STEP_TITLE
};

// any other kind names the request that failed
export const getReservationFailedTitle = (error: Pick<IReservationError, "kind" | "phase">) =>
    RESERVATION_FAILED_TITLES[error.kind] ?? (error.phase === "confirm" ? "Nie udało się potwierdzić rezerwacji" : "Nie udało się rozpocząć rezerwacji");

export const RESERVATION_RETRY_BUTTON_TEXT = "Spróbuj ponownie";
export const RESERVATION_CLOSE_BUTTON_TEXT = "Zamknij";

// Confirmation

export const RESERVATION_CONFIRMED_TITLES: Record<ReservationConfirmedVariant, string> = {
    reservation_24h: "Zarezerwowane na 24 godziny",
    submitted: "Rezerwacja złożona!"
};

export const RESERVATION_CONFIRMED_BUTTON_TEXT = "Rozumiem";
// the button as it is shown, in capitals - reported by the events of the confirmation's close
export const RESERVATION_CONFIRMED_BUTTON_LABEL = RESERVATION_CONFIRMED_BUTTON_TEXT.toUpperCase();
import {phoneNumberFormat} from "@pg-mono/string-utils";
import {CONSULTANT_PHONE_NATIONAL} from "@pg-rp/consultants";

import {ReservationConfirmedVariant} from "../types/IReservationConfirmedStepContentProps";
import {IReservationError, ReservationErrorKind} from "../types/IReservationError";
import {FULL_NAME_PART_MAX_LENGTH} from "./reservation_constants";

// the copy links it with CONSULTANT_PHONE_HREF
export const RESERVATION_HOTLINE = phoneNumberFormat(CONSULTANT_PHONE_NATIONAL);

// m:ss, e.g. "0:42"
export const formatReservationCountdown = (seconds: number) => {
    const wholeSeconds = Math.max(0, Math.ceil(seconds));

    return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, "0")}`;
};

// Code step

export const RESERVATION_CODE_STEP_TITLE = "Wpisz kod z e-maila";
// label of the dialog and of its loader while the first code is being sent - the title is not rendered yet
export const RESERVATION_SENDING_CODE_LABEL = "Wysyłamy kod";

export const RESERVATION_RESEND_QUESTION = "Nie dostałeś kodu?";
export const RESERVATION_RESEND_LINK_TEXT = "Wyślij ponownie";
export const RESERVATION_RESEND_EXHAUSTED_TEXT = `Limit kodów wyczerpany — spróbuj za 10 minut lub zadzwoń ${RESERVATION_HOTLINE}.`;
// announced once a resent code is on its way
export const RESERVATION_NEW_CODE_SENT_TEXT = "Wysłaliśmy nowy kod.";

// Countdowns - the ticking m:ss is rendered between `before` and `after`, apart from the text, so that it stays out of translation
export const RESERVATION_RESEND_COOLDOWN_COPY = {before: "Wyślij ponownie za ", after: ""};
export const RESERVATION_RESEND_BLOCKED_COPY = {before: "Zbyt wiele prób — spróbuj ponownie za ", after: "."};
// a rate-limited start counts down until "Spróbuj ponownie" becomes available
export const RESERVATION_RATE_LIMITED_COPY = {before: "Odczekaj ", after: " i spróbuj ponownie."};
export const RESERVATION_RATE_LIMIT_OVER_TEXT = "Możesz już spróbować ponownie.";

export const RESERVATION_DATA_ADMINISTRATOR_TEXT = "Administratorem danych jest Property Group sp. z o.o.";
export const RESERVATION_TERMS_LINK_TEXT = "Regulamin";

// Name step - shown when the API rejects the reservation for the profile name

export const RESERVATION_NAME_STEP_TITLE = "Uzupełnij swoje dane";
export const RESERVATION_NAME_STEP_DESCRIPTION = "Aby zarezerwować mieszkanie, podaj imię i nazwisko — przekażemy je deweloperowi.";
export const RESERVATION_NAME_INPUT_LABEL = "Imię i nazwisko";
export const RESERVATION_NAME_SUBMIT_BUTTON_TEXT = "Zapisz i wyślij kod";
export const RESERVATION_NAME_REQUIRED_TEXT = "Podaj imię i nazwisko.";
export const RESERVATION_NAME_TOO_LONG_TEXT = `Imię i nazwisko mogą mieć najwyżej ${FULL_NAME_PART_MAX_LENGTH} znaków każde.`;
export const RESERVATION_NAME_SAVE_FAILED_TEXT = "Nie udało się zapisać danych. Spróbuj ponownie.";

// Errors

// `IReservationError.message` per kind - a short plain-text message from the API replaces it for some kinds
export const RESERVATION_ERROR_MESSAGES: Record<ReservationErrorKind, string> = {
    invalid_code: "Nieprawidłowy kod — sprawdź e-mail i spróbuj ponownie.",
    expired: "Kod wygasł — wyślij ponownie.",
    taken: "Ta nieruchomość została już zarezerwowana.",
    user_limit: "Wykorzystałeś limit rezerwacji tej nieruchomości.",
    not_reservable: "Tej nieruchomości nie można teraz zarezerwować.",
    rate_limited: "Odczekaj chwilę i spróbuj ponownie.",
    not_verified: `Zweryfikuj konto, aby zarezerwować bezpłatnie. Pomoc: ${RESERVATION_HOTLINE}.`,
    profile_incomplete: "Uzupełnij imię i nazwisko w profilu, aby zarezerwować bezpłatnie.",
    rejected: `Nie możemy teraz zarezerwować tego lokalu. Pomoc: ${RESERVATION_HOTLINE}.`,
    unauthenticated: "Zaloguj się, aby zarezerwować bezpłatnie.",
    csrf: "Sesja wygasła — odśwież stronę i spróbuj ponownie.",
    forbidden: `Nie masz uprawnień do rezerwacji tego lokalu. Pomoc: ${RESERVATION_HOTLINE}.`,
    validation: "Sprawdź dane i spróbuj ponownie.",
    network: "Brak połączenia. Sprawdź internet i spróbuj ponownie.",
    server: "Chwilowy problem po stronie systemu rezerwacji. Spróbuj ponownie za chwilę.",
    unknown: `Spróbuj ponownie lub zadzwoń ${RESERVATION_HOTLINE}.`
};

// the expired-code copy asks for a resend, which is gone once every code of the CRM's window has been sent
export const RESERVATION_EXPIRED_WITHOUT_RESEND_TEXT = "Kod wygasł.";

const RESERVATION_FAILED_TITLES: Partial<Record<ReservationErrorKind, string>> = {
    rate_limited: "Zbyt wiele prób",
    // not about somebody else - the reservation can be the user's own earlier one
    taken: "Lokal jest już zarezerwowany",
    user_limit: "Limit rezerwacji wykorzystany",
    not_reservable: "Rezerwacja niedostępna",
    not_verified: "Twoje konto wymaga weryfikacji",
    profile_incomplete: RESERVATION_NAME_STEP_TITLE
};

// any other kind names the request that failed
export const getReservationFailedTitle = (error: Pick<IReservationError, "kind" | "phase">) =>
    RESERVATION_FAILED_TITLES[error.kind] ?? (error.phase === "confirm" ? "Nie udało się potwierdzić rezerwacji" : "Nie udało się rozpocząć rezerwacji");

export const RESERVATION_RETRY_BUTTON_TEXT = "Spróbuj ponownie";
export const RESERVATION_CLOSE_BUTTON_TEXT = "Zamknij";

// Confirmation

export const RESERVATION_CONFIRMED_TITLES: Record<ReservationConfirmedVariant, string> = {
    reservation_24h: "Zarezerwowane na 24 godziny",
    submitted: "Rezerwacja złożona!"
};

export const RESERVATION_CONFIRMED_BUTTON_TEXT = "Rozumiem";
// the button as it is shown, in capitals - reported by the events of the confirmation's close
export const RESERVATION_CONFIRMED_BUTTON_LABEL = RESERVATION_CONFIRMED_BUTTON_TEXT.toUpperCase();
